import { useEffect, useState } from 'react';
import { type Location, NavLink, useLocation } from 'react-router-dom';

import { Col, Container, Row } from 'react-bootstrap';

import { useAppSelector } from '@/ui/store/helperRedux';

import OrdersController from '@/controllers/OrdersController';

import { IBreadcrumbs } from '@/domain/interfaces/IBreadcrumbs';
import { IOrderData, IOrderProductsDetails } from '@/domain/interfaces/IOrder';

import { ScreenSize, useScreenSize } from '@/ui/hooks/useScreenSize';

import { getRoutesDirections } from '@/data/libraries/Routes';
import { StatusCodes, getStatusList } from '@/data/libraries/Status';
import { ConfigNumberParameters } from '@/data/libraries/ConfigParameters';
import lastPageName from '@/domain/utils/lastPageName';
import currencyFormat from '@/domain/utils/currencyFormat';
import dateFormat from '@/domain/utils/dateFormat';
import capitalize from '@/domain/utils/capitalize';
import { translate } from '@/domain/utils/translate';

import ErrorImage from '@/ui/assets/ErrorImage';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import Breadcrumbs from '@/ui/components/Breadcrumbs';
import IconList from '@/ui/components/IconList';
import PillText from '@/ui/components/PillText';
import PurchasesLoading from '@/ui/pages/user/Purchases/PurchasesLoading';
import EmptyContent from '@/ui/components/EmptyContent';


const Purchases = () => {

    /** Hook que contiene las dimensiones de la pantalla y maneja los componentes de acuerdo al resultado obtenido  */
    const { width, maxLargeWidth }: ScreenSize = useScreenSize();

    const StatusList = getStatusList();

    /** Controlador para las ordenes */
    const { getOrderByCustomer } = OrdersController();

    /** Estado Redux del usuario */
    const userLogued = useAppSelector((state) => state.userState);

    /** Funciones de formato de fecha*/
    const { formatZonedDateTimeToString } = dateFormat();

    /** Variable que define si es entorno mobile o desktop */
    const isMobile: boolean = width < maxLargeWidth;

    /** Estado inicial de la interfaz de loading */
    const [loading, setLoading] = useState<boolean>(true);

    /** Hook para trabajar con document location  */
    const location: Location = useLocation();

    /** Rutas URLs */
    const routesDirections = getRoutesDirections();

    /** Listado de breadcrumbs de la página */
    const breadcrumbsList: IBreadcrumbs[] = [
        { label: translate('breadcrumbs.home'), url: routesDirections.MAIN_ROUTE },
        { label: lastPageName(location.pathname), url: location.pathname },
    ];

    /** State de ordenes por cliente */
    const [orders, setOrders] = useState<IOrderData[]>([]);

    /** Obtiene ordenes de cliente logeado */
    const getPurchases = async (): Promise<void> => {
        setLoading(true);
        try {
            const ordersResponse = await getOrderByCustomer(userLogued.user.id ?? 0, userLogued.user.token ?? '');
            if (ordersResponse !== null) {
                setOrders(ordersResponse.sort((a, b) => b.id - a.id));
            }
        } catch (error) {
            console.error(error)
        }
        setLoading(false);
    };

    useEffect(() => {
        getPurchases();
    }, []);

    /** Componente Productos de orden */
    interface IProductListProps {
        products: IOrderProductsDetails[];
    };
    const ProductList = ({ products }: IProductListProps): JSX.Element => {

        /** Estabelce los productos a mostrar */
        const productsToShow: IOrderProductsDetails[] = [];
        if (isMobile && products.length > 0) {
            productsToShow.push(products[0]);
        } else if (products.length >= 4) {
            productsToShow.splice(0, 0, ...products.slice(0, 4));
        } else {
            productsToShow.splice(0, 0, ...products);
        }

        return (
            <Row className='d-flex justify-content-between'>
                {productsToShow.map((product, index) => (
                    <Col
                        key={index + 1}
                        className={`text-start d-flex justify-content-center align-items-center ${isMobile ? 'mb-1' : ''}`}
                    >
                        <Col className='d-flex flex-row mx-1'>
                            <Col lg={4} xs={4} >
                                <LazyLoadImage
                                    src={product.image}
                                    width={80}
                                    height={80}
                                    effect="blur"
                                    placeholderSrc={ErrorImage}
                                    onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                                        e.currentTarget.src = ErrorImage;
                                    }}
                                />
                            </Col>
                            <Col lg={8} xs={8} className='px-1'>
                                <span className='text-secondary-1 paragraph' >
                                    {product.title}
                                </span>
                            </Col>
                        </Col>
                    </Col>
                ))}
                {/* Rellenar columnas vacías si hay menos de 4 productos */}
                {!isMobile && Array.from({ length: 4 - productsToShow.length }).map((_, index) => (
                    <Col
                        key={`placeholder-${index}`}
                        className='d-flex justify-content-center align-items-center'
                    />
                ))}
                <Col
                    sm={1}
                    className='d-flex justify-content-end align-items-center pb-2'
                >
                    {(isMobile && products.length > 1) || (!isMobile && products.length > 4) ? (
                        <PillText
                            name={translate("purchases.products_text")}
                        />
                    ) : (
                        // Columna vacía si no hay más de 4 productos
                        <div></div>
                    )}
                </Col>

            </Row>
        );
    }

    /** Componente principal */
    return (
        <>
            {loading ? (
                <PurchasesLoading />
            ) : (
                <Container fluid className={`bg-primary-5 ${isMobile ? 'p-0' : 'p-0 min-vh-90 px-5'}`}>
                    <Row className={`g-0 ${isMobile ? '' : 'px-5 mx-4'}`}>
                        <Row className='pt-4 g-0 '>
                            <Col lg={12} className='p-2 bg-secondary-5 shadow rounded'>
                                <Breadcrumbs list={breadcrumbsList} />
                            </Col>
                        </Row>
                        <Row className='g-0 bg-secondary-5 shadow rounded my-3 py-2'>
                            <Col lg={12} className='p-2'>
                                <IconList

                                    classMain='ps-3 text-secondary-1'
                                    classIcon='svg-secondary-1 '
                                    title={translate("purchases.purchases_text")}
                                    iconName='ico-purchase'
                                    classTitle={`text-secondary-1 ${isMobile ? 'h5' : 'p-large-bold'} px-3`}
                                />
                            </Col>
                        </Row>
                        {orders.length === 0 &&
                            <Row className='min-vh-65 g-0'>
                                <EmptyContent />
                            </Row>
                        }
                        {orders.map((order, index) => {

                            /** Prepara la fecha de entrega si existe */
                            let deliveredDate: string = '';
                            if (order.ordStatusFlow) {
                                deliveredDate = order.ordStatusFlow.find(status => status.stateId === StatusCodes.ORDER_DELIVERED)
                                    ?.dateStateChange.split(' ')[0].trim() ?? '';
                            }

                            /** Prepara el Nombre del estado */
                            let statusName: string = '';
                            const status = StatusList.find((status) => status.id === order.status);
                            if (status) {
                                statusName = capitalize(status.name);
                            }

                            return (
                                <Row key={index + 1} className='g-0 bg-secondary-5 shadow rounded px-2 mb-4'>
                                    <Col lg={12} className={isMobile ? 'pt-4' : 'p-4'}>
                                        <ProductList products={order.orderProducts} />
                                    </Col>
                                    <Col className={`${!isMobile && 'p-3'} bg-secondary-4 rounded-2 mb-4 d-flex ${isMobile ? 'flex-column px-3 py-3' : 'flex-row'} `}>
                                        <Col className={`d-flex ${isMobile ? 'justify-content-between align-items-center' : 'flex-column align-items-start px-3'}`} >
                                            <IconList
                                                classIcon='pe-2'
                                                iconName='ico-data'
                                                title={translate("purchases.order_title")}
                                                classTitle={`text-secondary-1 ${isMobile ? 'p-large' : 'paragraph'} fw-bold`}
                                            />
                                            <span className={`${!isMobile && 'mt-3'}`}> {order.ordCodeNum}</span>
                                        </Col >
                                        <div className={`${!isMobile && 'vr'}`} />
                                        <Col className={`d-flex ${isMobile ? 'justify-content-between align-items-center' : 'flex-column align-items-start px-3'}`} >
                                            <IconList
                                                classIcon='pe-2'
                                                iconName='ico-calendar'
                                                title={translate("purchases.date_title")}
                                                classTitle={`text-secondary-1 ${isMobile ? 'p-large' : 'paragraph'} fw-bold`}
                                            />
                                            <span className={`${!isMobile && 'mt-3'}`}> {formatZonedDateTimeToString(order.date)}</span>
                                        </Col>
                                        {order.status === StatusCodes.ORDER_DELIVERED && <>
                                            <div className={`${!isMobile && 'vr'}`} />
                                            <Col className={`d-flex ${isMobile ? 'justify-content-between align-items-center' : 'flex-column align-items-start px-3'}`} >
                                                <IconList
                                                    classIcon='pe-2'
                                                    iconName='ico-calendar'
                                                    title={translate("purchases.shipping_date_title")}
                                                    classTitle={`text-secondary-1 ${isMobile ? 'p-large' : 'paragraph'} fw-bold`}
                                                />
                                                <span className='mt-3'>{deliveredDate}</span>
                                            </Col>
                                        </>}
                                        <div className={`${!isMobile && 'vr'}`} />
                                        <Col className={`d-flex ${isMobile ? 'justify-content-between align-items-center ps-1' : 'flex-column align-items-start px-3'}`} >
                                            <IconList
                                                classIcon='pe-2'
                                                iconName='ico-total'
                                                title={translate("purchases.amount_title")}
                                                classTitle={`text-secondary-1 ${isMobile ? 'p-large' : 'paragraph'} fw-bold`}
                                            />
                                            <span
                                                className={`${!isMobile && 'mt-3'}`}> {currencyFormat(order?.totalAmount, ConfigNumberParameters.DECIMALS_ALLOWED)}
                                            </span>
                                        </Col>
                                        <div className={`${!isMobile && 'vr'}`} />
                                        <Col className={`d-flex ${isMobile ? 'justify-content-between align-items-center' : 'flex-column align-items-start px-3'}`} >
                                            <IconList
                                                classIcon='pe-2'
                                                iconName='ico-shipping-area'
                                                title={translate("purchases.status_title")}
                                                classTitle={`text-secondary-1 ${isMobile ? 'p-large' : 'paragraph'} fw-bold`}
                                            />
                                            <span className={`${!isMobile && 'mt-3'}`}> {statusName}</span>
                                        </Col>
                                        <Col
                                            lg={3}
                                            className={`text-primary-1 d-flex align-items-center ${isMobile ? 'pt-3 px-3' : 'justify-content-end'}`}
                                        >
                                            <NavLink
                                                to={`${routesDirections.TRACKING_ROUTE_DETAIL}?order=${order.ordCodeNum}`}
                                                className={`${isMobile && 'w-100 text-center'} btn-secondary-icon-small px-5 py-2 fw-bold`}
                                            >
                                                <span className='h5'>{translate("purchases.details_text")}</span>
                                            </NavLink>
                                        </Col>

                                    </Col>
                                </Row>
                            );
                        })}
                    </Row>

                </Container>
            )}
        </>
    );
};

export default Purchases;
